import { useState, useEffect } from 'react';
import {
  fetchFramework,
  fetchFrequency,
  getAllUsers,
  getReportingAnswer,
  getReportingModules,
  getReportingQuestions,
  getSource,
} from '../../Training/training-dashboard/services/trainingService';

/**
 * Fetches all base dashboard data for the selected financial year in one parallel batch:
 * locations, company frequency, framework/module options, reporting questions/answers and users.
 * Owns the shared `loading`/`error` flags (setters returned so downstream effects can update them).
 *
 * @param {number|string} financialYearId
 * @returns source data + { loading, error, setLoading, setError }
 */
export const useDashboardSourceData = (financialYearId) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [frameworkOptions, setframeworkOptions] = useState([]);
  const [moduleOptions, setModuleOptions] = useState([]);
  const [reportingQuestions, setReportingQuestions] = useState([]);
  const [reportingAssignDetails, setReportingAssignDetails] = useState([]);
  const [reportingAnswers, setReportingAnswer] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  /**
   * `frequency` is the company-level default period type fetched from the API.
   */
  const [frequency, setFrequency] = useState('MONTHLY');

  useEffect(() => {
    let isMounted = true;
    const initializeData = async () => {
      try {
        if (isMounted) setLoading(true);
        if (financialYearId) {
          const [locations, frequencyData, frameworkOptions, moduleOptions, reportingQuestion, reportingAnswer, allUsers] = await Promise.all([
            getSource(),
            fetchFrequency(financialYearId),
            fetchFramework(),
            getReportingModules(financialYearId),
            getReportingQuestions(financialYearId),
            getReportingAnswer(financialYearId),
            getAllUsers(),
          ]);
          if (isMounted) {
            const options = (frameworkOptions || []).map(item => ({
              value: item.id,
              label: item?.display_name || item?.title
            }));
            setframeworkOptions(options);
            setLocations(locations);
            setFrequency(frequencyData);
            setModuleOptions(moduleOptions);
            setReportingQuestions(reportingQuestion.data);
            setReportingAssignDetails(reportingQuestion.assignedDetail);
            setReportingAnswer(reportingAnswer);
            setAllUsers(allUsers);
          }
        }
      } catch (err) {
        if (isMounted) setError(err.message);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    initializeData();
    return () => {
      isMounted = false;
    };
  }, [financialYearId]);

  return {
    loading,
    error,
    setLoading,
    setError,
    frameworkOptions,
    moduleOptions,
    reportingQuestions,
    reportingAssignDetails,
    reportingAnswers,
    allUsers,
    locations,
    frequency,
  };
};
